// One-time import of the OLD React web app's localStorage data into the
// brain's KV world (docs/09 §10): "the webview host runs
// migrateLegacyLocalStorage() — copies the old React app's localStorage keys
// (favorites, aliases, callLog, dev origin, language) into KV, marks done,
// leaves originals." Idempotent via a marker KV key (safe to call on every
// cold boot), and KV always wins: a destination slot that already has data —
// a real device write, or a previous partial run — is left untouched, never
// clobbered by a stale legacy copy. Never mutates `storage`: the originals
// stay in place so a rollback never loses data.
//
// The legacy key names + value formats are read from (never imported from —
// the brain must not depend on `frontend/app`) the app modules that still own
// them: storage/favorites.ts, storage/aliases.ts, storage/callLog.ts,
// shell/devServer.ts, i18n/core.ts. The destination slots are the ones the
// brain's own implementations read: runtime/favorites.ts, runtime/aliases.ts,
// runtime/callLog.ts. Dev origin, dev-unlocked and language stay host-local
// settings (docs/09 §11) with no brain wrapper of their own, so they migrate
// under their existing key names unchanged.

import { z } from 'zod';
import { CallLogEntry } from '@merkury/protocol';
import type { KvStore } from './kv.js';

// --- legacy (localStorage) key names ------------------------------------------

const LEGACY_FAVORITES_KEY = 'merkury.favorites';
const LEGACY_ALIASES_KEY = 'merkury.aliases';
const LEGACY_CALL_LOG_KEY = 'merkury.calllog.v1';
const LEGACY_DEV_ORIGIN_KEY = 'merkury.dev.server';
const LEGACY_DEV_UNLOCKED_KEY = 'merkury.dev.unlocked';
const LEGACY_LANG_KEY = 'app-language';

// --- destination (KV) key names -------------------------------------------------

const FAVORITES_KEY = 'favorites'; // runtime/favorites.ts FAVORITES_KEY
const ALIASES_KEY = 'merkury.aliases.v1'; // runtime/aliases.ts ALIASES_KEY
const CALL_LOG_KEY = 'callLog'; // runtime/callLog.ts CALL_LOG_KEY
const DEV_ORIGIN_KEY = LEGACY_DEV_ORIGIN_KEY; // host-local setting, same slot name
const DEV_UNLOCKED_KEY = LEGACY_DEV_UNLOCKED_KEY; // host-local setting, same slot name
const LANG_KEY = LEGACY_LANG_KEY; // i18n/core.ts LANG_KEY, same slot name

/** Marks the one-time import done; never re-read after that (this module's
 *  own concern — no other code needs this key). */
const MIGRATION_MARKER_KEY = 'legacyLocalStorageMigrated';

// --- legacy value parsing (mirrors each app module's own tolerant `load()`) ----

/** Mirrors storage/favorites.ts `load()`: non-array or unparsable JSON is
 *  malformed; a valid array keeps only its string entries (silently, like the
 *  app does — the app never treated a stray non-string entry as corruption). */
function parseLegacyFavorites(raw: string): string[] | undefined {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return undefined;
  }
  return Array.isArray(parsed)
    ? parsed.filter((v): v is string => typeof v === 'string')
    : undefined;
}

/** Mirrors storage/aliases.ts `load()`: unparsable JSON or a non-object
 *  (including arrays) is malformed; a valid object keeps only its non-empty
 *  string values. */
function parseLegacyAliases(raw: string): Record<string, string> | undefined {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return undefined;
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return undefined;
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
    if (typeof v === 'string' && v.length > 0) out[k] = v;
  }
  return out;
}

/** Device-local extension of the protocol `CallLogEntry` (storage/callLog.ts
 *  `StoredCallLogEntry`), re-declared here rather than imported so this module
 *  stays independent of both `frontend/app` and `runtime/callLog.ts`. */
const LegacyCallLogEntry = CallLogEntry.extend({ peerName: z.string().min(1).optional() });
type LegacyCallLogEntry = z.infer<typeof LegacyCallLogEntry>;
const LegacyCallLogSnapshot = z.array(LegacyCallLogEntry);

/** Mirrors storage/callLog.ts `load()`: whole-array validation — a single bad
 *  entry invalidates the lot (matches the app's own `Snapshot.safeParse`
 *  all-or-nothing behavior) rather than silently dropping rows from history. */
function parseLegacyCallLog(raw: string): LegacyCallLogEntry[] | undefined {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return undefined;
  }
  const result = LegacyCallLogSnapshot.safeParse(parsed);
  return result.success ? result.data : undefined;
}

/** shell/devServer.ts only ever stores a normalized non-empty origin string
 *  (or removes the key) — anything else on disk is corruption. */
function parseLegacyDevOrigin(raw: string): string | undefined {
  return raw.length > 0 ? raw : undefined;
}

/** shell/devServer.ts only ever stores the literal `'1'` (or removes the key)
 *  — `isDevUnlocked()` treats anything else as not-unlocked, so any other
 *  stored value is corruption, not a legitimate "false". */
function parseLegacyDevUnlocked(raw: string): true | undefined {
  return raw === '1' ? true : undefined;
}

const LANG_SETTINGS = new Set(['system', 'en', 'ru']);

/** i18n/core.ts `LangSetting` — only these three literals are ever stored. */
function parseLegacyLanguage(raw: string): string | undefined {
  return LANG_SETTINGS.has(raw) ? raw : undefined;
}

// --- driver ------------------------------------------------------------------

export interface MigrationReport {
  /** Store names actually copied into KV this call. */
  migrated: string[];
  /** Store names whose legacy value existed but failed to parse — left as-is
   *  in `storage`, never written to KV. */
  skipped: string[];
  /** True when a previous call already finished the migration; nothing in
   *  `storage` or `kv` was read or written this call. */
  alreadyDone: boolean;
}

/**
 * The slice of `Storage` this helper needs to READ. Deliberately omits
 * `clear()` — the whole point of this helper is that legacy data is never
 * destroyed, only copied — even though callers typically pass the real
 * `localStorage` (which has it).
 */
export type LegacyStorageLike = Pick<
  Storage,
  'getItem' | 'setItem' | 'removeItem' | 'key' | 'length'
>;

interface MigrationItem {
  name: string;
  legacyKey: string;
  destKey: string;
  parse: (raw: string) => unknown;
}

const ITEMS: readonly MigrationItem[] = [
  {
    name: 'favorites',
    legacyKey: LEGACY_FAVORITES_KEY,
    destKey: FAVORITES_KEY,
    parse: parseLegacyFavorites,
  },
  {
    name: 'aliases',
    legacyKey: LEGACY_ALIASES_KEY,
    destKey: ALIASES_KEY,
    parse: parseLegacyAliases,
  },
  {
    name: 'callLog',
    legacyKey: LEGACY_CALL_LOG_KEY,
    destKey: CALL_LOG_KEY,
    parse: parseLegacyCallLog,
  },
  {
    name: 'devOrigin',
    legacyKey: LEGACY_DEV_ORIGIN_KEY,
    destKey: DEV_ORIGIN_KEY,
    parse: parseLegacyDevOrigin,
  },
  {
    name: 'devUnlocked',
    legacyKey: LEGACY_DEV_UNLOCKED_KEY,
    destKey: DEV_UNLOCKED_KEY,
    parse: parseLegacyDevUnlocked,
  },
  {
    name: 'language',
    legacyKey: LEGACY_LANG_KEY,
    destKey: LANG_KEY,
    parse: parseLegacyLanguage,
  },
];

/**
 * Copy the old web app's localStorage data into `kv`, once. Safe to call on
 * every cold boot: a completed run marks itself done and every later call
 * returns immediately (`alreadyDone: true`) without touching `storage` or
 * `kv` again. Never throws — a malformed legacy value is counted in
 * `skipped` and left in `storage` untouched, exactly like a missing one.
 */
export async function migrateLegacyLocalStorage(
  kv: KvStore,
  storage: LegacyStorageLike,
): Promise<MigrationReport> {
  if ((await kv.get<boolean>(MIGRATION_MARKER_KEY)) === true) {
    return { migrated: [], skipped: [], alreadyDone: true };
  }

  const migrated: string[] = [];
  const skipped: string[] = [];

  for (const item of ITEMS) {
    const raw = storage.getItem(item.legacyKey);
    if (raw === null) continue; // nothing to import for this store

    // KV wins: a slot that already holds data — a real device write, or a
    // previous partial run — is never clobbered by the legacy copy.
    if ((await kv.get(item.destKey)) !== undefined) continue;

    const value = item.parse(raw);
    if (value === undefined) {
      skipped.push(item.name);
      continue;
    }
    await kv.put(item.destKey, value);
    migrated.push(item.name);
  }

  await kv.put(MIGRATION_MARKER_KEY, true);
  return { migrated, skipped, alreadyDone: false };
}
